import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { map } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { TenantOptions, TenantOptionsValidationMode } from '../models/tenant-options';
import { MISSING_TENANT_OPTION_REASON } from '../constants/tenant-option.constants';
import { hasRequiredTenantOptions } from '../utils/tenant-options.utils';

/**
 * Tenant option guard that checks if the tenant has the required contracted products
 * to access a route.
 *
 * Usage in routes:
 * ```
 * {
 *   path: 'analise-de-credito',
 *   canActivate: [authGuard, tenantOptionGuard],
 *   data: { requiredTenantOptions: [TenantOptions.CREDIT_NEW_CREDIT_ANALYSIS] }
 * }
 * ```
 *
 * If requiredTenantOptions is not present or empty, access is allowed.
 * The default mode is 'all' — use 'any' when a single option is enough.
 */
export const tenantOptionGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const requiredTenantOptions: TenantOptions[] = route.data?.['requiredTenantOptions'];
  const mode: TenantOptionsValidationMode = route.data?.['requiredTenantOptionsMode'] || 'all';

  if (!requiredTenantOptions || requiredTenantOptions.length === 0) {
    return true;
  }

  return authService.ensureUserLoaded().pipe(
    map((loaded) => {
      if (!loaded) {
        return false;
      }

      const hasAccess = hasRequiredTenantOptions(
        (option) => authService.hasTenantOption(option),
        requiredTenantOptions,
        mode,
      );

      if (hasAccess) {
        return true;
      }

      return router.createUrlTree(['/nao-autorizado'], {
        queryParams: { from: state.url, reason: MISSING_TENANT_OPTION_REASON },
      });
    }),
  );
};
